import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api.js';
import { Spinner, ErrorNote } from '../../components.jsx';

function groupByJob(assignments) {
  const groups = new Map();
  for (const a of assignments) {
    const key = a.jobNumber || a.jobName || 'Unassigned';
    if (!groups.has(key)) groups.set(key, { jobNumber: a.jobNumber, jobName: a.jobName, staff: [] });
    groups.get(key).staff.push(a);
  }
  return [...groups.values()];
}

export default function ScheduleWidget() {
  const [day, setDay] = useState('today');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/api/widgets/schedule').then(setData).catch((e) => setError(e.message));
  }, []);

  const assignments = useMemo(() => (data ? data.assignments.filter((a) => a.day === day) : []), [data, day]);
  const jobs = useMemo(() => groupByJob(assignments), [assignments]);
  const onLeave = useMemo(() => (data ? data.leave.filter((l) => l.day === day) : []), [data, day]);

  return (
    <div className="card flex flex-col p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="min-w-0 truncate text-sm font-bold uppercase tracking-wide text-steel">Staff schedule</h2>
        <div className="flex shrink-0 gap-1 rounded-lg bg-steel/10 p-0.5">
          {['today', 'tomorrow'].map((d) => (
            <button
              key={d}
              onClick={() => setDay(d)}
              className={`rounded-md px-2 py-1 text-xs font-medium capitalize transition ${day === d ? 'bg-navy text-white' : 'text-steel hover:bg-steel/10'}`}
            >
              {d}
            </button>
          ))}
        </div>
      </div>
      {error && <ErrorNote>Couldn't reach the scheduler ({error})</ErrorNote>}
      {!data && !error && <Spinner label="Loading schedule…" />}
      {data && jobs.length === 0 && <p className="text-sm text-steel">No allocations found.</p>}
      {data && jobs.length > 0 && (
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
          {jobs.map((job) => (
            <div key={`${job.jobNumber}-${job.jobName}`}>
              <div className="mb-1 text-xs font-bold text-steel">
                {job.jobNumber ? `${job.jobNumber} — ` : ''}{job.jobName || 'Unassigned'}
              </div>
              <ul className="space-y-0.5">
                {job.staff.map((s, i) => (
                  <li key={i} className="flex items-center justify-between gap-2 text-sm">
                    <span className="min-w-0 truncate">{s.staffName}</span>
                    {s.hours != null && <span className="shrink-0 text-xs text-steel">{s.hours}h</span>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      {data && onLeave.length > 0 && (
        <div className="mt-3 border-t border-steel/10 pt-3">
          <div className="mb-1 text-xs font-bold uppercase tracking-wide text-steel">On leave</div>
          <div className="flex flex-wrap gap-1.5">
            {onLeave.map((l, i) => (
              <span key={i} className="badge bg-amber/30 text-charcoal dark:text-amber">{l.staffName} · {l.leaveType}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
